import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ProductCoverageAddressModel } from 'src/app/activation-workflow/shared/models/product-coverage.model';
import { ActivationFlow } from '../../catalog/components/capabilities/capabilities-constant';
import { CORE_CONFIGURATION } from '../../core/configuration';
import { buildAwsApi, setObjectParams } from '../util.class';
import { ApiModule } from './api.module';
import { BaseApiService } from './base-api.service';
import { ValidAddress } from './geographic-address-mgt-api.service';
import { MailDataModel, TaggingDataModel } from '../click-to-sell/click-to-sell.model';
import { SessionState, SessionStateModel } from '../session';
import { Store } from '@ngxs/store';

@Injectable({
  providedIn: ApiModule,
  deps: [BaseApiService],
})
export class CatalogApiService {
  keyMapAWS: any;

  constructor(private http: BaseApiService, @Inject(CORE_CONFIGURATION) private config,private store: Store) {
    const { keyMapAWS }: SessionStateModel = this.store.selectSnapshot(SessionState);
    this.keyMapAWS = keyMapAWS;
  }

  getAllTabs(
    hideError = false,
    productType: string = 'all',
    offerId = '',
  ): Observable<any>{
    const apiAWS = buildAwsApi(this.keyMapAWS, 'catalog_activable_editorial_tabs', {offerId:offerId,productType:productType});

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS,
      awsApi: apiAWS !== null,
      hideError
    });
  }

  getEditorialOffers(
    hideError = false,
    productType: string = 'editorial',
    offerId = '',
  ): Observable<any>{
    const apiAWS = buildAwsApi(this.keyMapAWS, 'catalog_activable_editorial_tabs', {offerId:offerId,productType:productType});

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS,
      awsApi: apiAWS !== null,
      hideError
    });
  }



  deactivationRequest(msisdn: string, username: string, productId: string) {
    const apiAWS = buildAwsApi(this.keyMapAWS, 'deactivation_post', {});
    const body = {msisdn:msisdn,username:username,productId:productId};
    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      awsApi: apiAWS !== null,
      body: body,
      path:apiAWS
    });
  }

  /**
   * catalog api get activable offers
   * @param msisdn current msisdn
   */
  offersActivable(msisdn: string, hideError = false): Observable<any> {

    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_activable_sim', {msisdn} );
    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-catalog/sim/${msisdn}/activable`,
      awsApi: apiAWS !== null,
      hideError
    });

    /*
    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: `/v2/products-catalog/sim/${msisdn}/activable`,
      hideError
    });
    */
  }

  fixedOffersActivable(hideError = false): Observable<any> {
    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_activable_landline', {offerId:'ALL'} );
    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? '/v2/products-catalog/landline/activable?offerId=ALL',
      awsApi: apiAWS !== null,
      hideError
    });
  }

  /**
   * catalog api get activable offers
   */
  offersCoreActivable(
    customerCode: string,
    hideError = false,
    productType: string = 'MOBILE',
    limit: string = '',
    offset: string = '',
    isSellable: string = 'Y',
    marketSegment: string = ''
  ): Observable<any> {
    let params = new HttpParams();

    // if keyMapAWS is present, we should use it!
    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_activable_company',
      {
        customerCode,
        productType,
        catalogType: 'MM',
        limit,
        offset,
        isSellable,
        marketSegment
      });

    if(!apiAWS) {
      params = params.append('productType', productType); // in order to get ONLY core offers
      params = params.append('catalogType', 'MM'); // in order to get ONLY mass-market offers
    };

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-catalog/company/${customerCode}/activable`,
      params,
      awsApi: apiAWS !== null,
      hideError
    });
  }

  /**
   * catalog api get activable offers
   */
  offersCoreProductDetail(
    customerCode: string,
    productSpecId: string,
    hideError = false,
    productType: string = 'MOBILE',
    limit: string = '',
    offset: string = '',
    isSellable: string = 'Y',
    marketSegment: string = ''
  ): Observable<any> {

    // if keyMapAWS is present, we should use it!
    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_activable_details_company',
      { customerCode,
        productSpecId,
        productType,
        catalogType: 'MM',
        limit,
        offset,
        isSellable,
        marketSegment
      });

    let params = new HttpParams();
    if(!apiAWS) {
      params = params.append('productType', productType); // in order to get ONLY core offers
      params = params.append('catalogType', 'MM'); // in order to get ONLY mass-market offers
    }

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-catalog/company/${customerCode}/activable/${productSpecId}/details`,
      params,
      awsApi: apiAWS !== null,
      hideError
    });
  }

  /**
   * productDetail api for detail page product data
   * @param msisdn number
   * @param productType product type
   * @param productId product id
   */
  offersProduct(msisdn: string, productType: string, productId: string): Observable<any> {

    // if keyMapAWS is present, we should use it!
    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_product',
      { msisdn,
        productType,
        productId
      });

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-catalog/sim/${msisdn}/products/${productType}/${productId}`,
      awsApi: apiAWS !== null,
    });
  }

  /**
   * productDetail api for detail page check activation data
   * @param msisdn number
   * @param type product type
   * @param id product id
   * @param username username
   */
  offerCheck(msisdn: string, type: string, id: string,username:string, hideError = false): Observable<any> {

    //if keyMapAWS is present, we should use it!
    const apiAWS = buildAwsApi(this.keyMapAWS,'activation_activation_get',
      {
        msisdn: msisdn,
        productType: type,
        productId: id,
        username:username
      });

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-activation-funnels/sim/${msisdn}/${type}/${id}/activation`,
      awsApi: apiAWS !== null,
      hideError
    });
  }

  /**
   * catalog api get activable offers
   */
  fixedOffersActivableDetail(productSpecId: string, hideError = false): Observable<any> {
    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_activable_landline', {offerId: productSpecId} );
    console.log('FIXED_OFFERID');
    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-catalog/landline/activable/${productSpecId}`,
      awsApi: apiAWS !== null,
      hideError
    });
  }

  /**
   * perform offer activation for classic flow
   * @param msisdn activation number
   * @param type activtion offer type
   * @param id activation offer id
   * @param username username
   */
  offerActivation(msisdn: string, type: string, id: string,username:string): Observable<any> {
    // if keyMapAWS is present, we should use it!
    const apiAWS = buildAwsApi(this.keyMapAWS,'activation_activation_post', {});
    const body = {
      msisdn: msisdn,
      productType: type,
      productId: id,
      username:username
    };

    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-activation-funnels/sim/${msisdn}/${type}/${id}/activation`,
      awsApi: apiAWS !== null,
      body
    });
  }

  /**
   * perform offer activation for G2GO flow
   * @param msisdn activation number
   * @param type activtion offer type
   * @param id activation offer id
   */
  offerC2goActivation(
    msisdn: string,
    type: string,
    id: string,
    payload: { secureNetwork: boolean; email: string }
  ): Observable<any> {
    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      path: `/v2/products-activation-funnels/sim/${msisdn}/${type}/${id}/activation/c2go`,
      body: payload,
    });
  }

  /**
   * perform offer activation for HardCover flow
   * @param msisdn activation number
   * @param type activtion offer type
   * @param id activation offer id
   */
  offerHardCoverActivation(msisdn: string, type: string, id: string): Observable<any> {
    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      path: `/v2/products-activation-funnels/sim/${msisdn}/${type}/${id}/_activation`,
    });
  }

  /**
   * perform offer booking activation for rtm flow
   * @param msisdn telephone number
   * @param type P | S | TM
   * @param mmId mass market id
   * @param simId sim id
   * @param offerCode identifier of proposition
   * @param flow activation type identifier
   * @param productName product name
   */
  offerRtmBookingActivation(
    msisdn: string,
    type: string,
    mmId: string,
    simId: string,
    offerCode: string,
    flow: string,
    productName: string
  ): Observable<any> {
    return this.http.post({
      path: '/ProductActivationManagement/v1/ProductActivationRequest',
      body: {
        simId,
        msisdn,
        type: flow === ActivationFlow.C2GO || flow === ActivationFlow.C2GO_CMB ? 'TM' : 'P',
        id: mmId,
        actionType: flow === ActivationFlow.C2GO || flow === ActivationFlow.C2GO_CMB || flow === ActivationFlow.NBA ? 'NBA' : 'RTM',
        offerCode,
        productName,
      },
    });
  }

  /**
   * api for send email for confirm activation
   * @param msisdn telephone number
   * @param type product type
   * @param id product id
   * @param username product id
   */
  sendEmail(msisdn: string, type: string, id: string,username:string, payload: { recipient: string }): Observable<any> {
    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      path: `/v2/products-activation-funnels/sim/${msisdn}/${type}/${id}/${username}/mail`,
      body: payload,
    });
  }

  /**
   * catalog api get active offers
   * @param msisdn current msisdn
   */
  activeOffers(msisdn: string, hideError = false): Observable<any> {
    // if keyMapAWS is present, we should use it!
    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_active', {msisdn} );

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-catalog/sim/${msisdn}/active`,
      awsApi: apiAWS !== null,
      hideError,
    });
  }

  /**
   * shoppingCart api for active offers
   * @param msisdn current msisdn
   */
  shoppingCart(
    msisdn: string,
    delegationCustCode: string,
    hideError = false,
    status: string = 'Pending',
    filter: string = '',
  ): Observable<any> {

    // if keyMapAWS is present, we should use it!
    const apiAWS = buildAwsApi(this.keyMapAWS,'activation_shoppingcart_get',
      {
        delegationCustCode,
        msisdn,
        status,
        filter
      });

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-catalog/sim/${msisdn}/shoppingCart?status=Pending`,
      awsApi: apiAWS !== null,
      hideError,
    });
  }

  /**
   * productActivationRefuse api for active offers shoopingCart product
   * @param msisdn current msisdn
   */
  productActivationRefuse({ simId, id, offerCode }: { simId: string; id: string; offerCode: string }): Observable<any> {
    return this.http.post({
      path: '/ProductActivationManagement/v1/ProductActivationRefuse',
      body: { simId, id, offerCode },
    });
  }

  contextSearch(msisdn: string, anchorId: string, resolutionId = 'c2cAnchor') {
    const apiAWS = buildAwsApi(this.keyMapAWS, 'contextSearch_fe', {});

    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? '/v2/anchors/contextSearch',
      awsApi: apiAWS !== null,
      body: {
        anchors: [
          {
            anchorId: anchorId,
            resolutionId,
            variables: {
              currentMenuItemId: 'settings',
            },
          },
        ],
        variables: {
          profileType: 'REFERENT',
          msisdn: msisdn,
          status: 'logged',
        },
      },
    });
  }

  /**
   * inventory api get active digital solutions
   */
  inventory(customerCode: string, username: string, hideError = false,name=''): Observable<any> {
    let params = new HttpParams();
    params = params.set('username', username);

    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_inventory', {username:username,customerCode:customerCode,name:name});
    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ??  `/v2/products-catalog/customer/${customerCode}/inventory`,
      awsApi: apiAWS !== null,
      hideError,
      params:apiAWS ? null : params
    });
  }

  /**
   * api service for upCrossSellBoost
   */
  setResponse(productId: string, body: any, simId: string): Observable<any> {
    const channelId = 'APP';

    return this.http.post({
      path: `/Recommendation/v2/proposition/${productId}?channelId=${channelId}&simId=${simId}`,
      hideError: true,
      body,
    });
  }

  /**
   * api for retrieve simId
   */
  simId(msisdn: string): Observable<any> {
    return this.http.get({
      path: `/ResourceInventoryManagementNg/v1/sim?msisdn=${msisdn}`,
    });
  }


  /**
      * @param hideError (default false) in order to manage custom errors
      */
  createNewShoppingCart(body: any, hideError = false): Observable<any> {
    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      path: '/v2/products-catalog/shoppingCart',
      hideError,
      body
    });
  }

  ////////////////////////
  //// FIXED-LINE ////////
  ////////////////////////

  /**
   * api service for check Product Coverage
   * @param productCoverage contain ValidAddress + offer information
   */
  checkProductCoverage(productCoverage: ProductCoverageAddressModel, customerCode: string){
    const apiAWS = buildAwsApi(this.keyMapAWS,'catalog_coverage',{customerCode:customerCode   ,streetNr:productCoverage.streetNr,streetName:productCoverage.streetName,postcode:productCoverage.postcode,city:productCoverage.city,stateOrProvince:productCoverage.stateOrProvince,addressCode:productCoverage.addressCode,productSpecID:productCoverage.productSpecID,productOfferingId:productCoverage.productOfferingId});
    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      awsApi: apiAWS !== null,
      path: apiAWS,
    });
  }

  /**
   * Request Data Encryption
   * @param objectType — sim or landline
   * @param objectRef — MSISDN or Link Account
   * @param body:
      * {
      *  skill: string;
      *  taggingData:TaggingDataModel;
      *  mailData: MailDataModel
      * }
   */
  dataEncryption(objectType: string, objectRef: string, body: any): Observable<any> {
    const apiAWS = buildAwsApi(this.keyMapAWS, 'activation_activate_c2go_post', {});

    if(apiAWS) {
      body = {
        ...body,
        objectType: objectType,
        objectRef: objectRef
      };
    }

    return this.http.post({
      globalErrorHandler: true,
      hideError:false,
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? `/v2/products-activation-funnels/activation/request/c2go/${objectType}/${objectRef}`,
      awsApi: apiAWS !== null,
      body
    });
  }


  ////////////////////////
  //// LIST-DEVICE ////////
  ////////////////////////

  /**
   * api service to retrive device list of an offer
   * @param productOfferingId offerId
   * @param resourceType can have three values:
      model → scenario 1 --> The scenario Resource category is used to have a list of resource category (models) enriched with the data of AEM.
      device → scenario 2 --> The scenario Resource category is used to have a list of salable resource candidate (devices) with the available details and the models.
      deviceOffering → scenario 3 --> The scenario Resource category is used to have a list of resource candidate (devices) and resource category (models) related to a productOffering.
    *
   */
  listDevice(productOfferingId: string, resourceType: string){
    let params = new HttpParams();
    params = params.set('productOfferingId', productOfferingId);
    params = params.set('resourceType', resourceType);
    params = params.set('mdp', 'Carta di Credito');

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      path: '/v2/products-catalog/company/retrieveDevice',
      params
    });
  }
}
